import React, { useState, useEffect } from "react";
import { Table, Tooltip } from "antd";
import PropTypes from "prop-types";

const PurchaseRequisitionSteelDetails = ({ prData }) => {
  const [tableData, setTableData] = useState([]);
  const [materialSizes, setMaterialSizes] = useState([]);
  const [diameterPrices, setDiameterPrices] = useState({});


  useEffect(() => {
    if (prData) {
      const sizes = new Set();
      const priceMap = {};
      prData.city_site_material_data.forEach((city) => {
        city.site_wise_pr.forEach((site) => {
          site.pr_material_details.forEach((material) => {
            material.steel_sizes.forEach((size) => {
              sizes.add(size.value);
              if (priceMap[size.value] === undefined) {
                priceMap[size.value] = size.estimated_price;
              }
            });
          });
        });
      });

      const sortedSizes = Array.from(sizes).sort((a, b) => a - b);
      setMaterialSizes(sortedSizes);
      setDiameterPrices(priceMap);

      const transformedData = [];
      let rowCounter = 1;

      let grandTotalEstimatedAmount = 0;

      prData.city_site_material_data.forEach((city, cityIndex) => {
        let cityTotalEstimatedAmount = 0;
        city.site_wise_pr.forEach((site, siteIndex) => {
          let siteTotalEstimatedAmount = 0;
          const seenSubCategories = new Set(); // Track sub-categories within this site

          site.pr_material_details.forEach((material, materialIndex) => {
            const diameterMap = {};
            material.steel_sizes.forEach((size) => {
              diameterMap[size.value] = {
                quantity: size.quantity,
                price: size.estimated_price,
              };
            });

            const estimatedAmount = material.steel_sizes.reduce(
              (sum, size) =>
                sum +
                (material.pr_material_type_uom_name === "MT"
                  ? size.quantity * 1000 * size.estimated_price
                  : size.quantity * size.estimated_price),
              0
            );

            siteTotalEstimatedAmount += estimatedAmount;
            cityTotalEstimatedAmount += estimatedAmount;
            grandTotalEstimatedAmount += estimatedAmount;

            // Only show sub-category if it's the first occurrence in this site
            const subCategory = material.pr_material_type_sub_category;
            const showSubCategory = !seenSubCategories.has(subCategory);
            seenSubCategories.add(subCategory);

            transformedData.push({
              key: `${cityIndex}-${siteIndex}-${materialIndex}`,
              srNo: siteIndex === 0 && materialIndex === 0 ? rowCounter : null,
              city: city.city_name,
              site: site.site_name,
              pr_material_type_sub_category: showSubCategory ? subCategory : "", // Show only if new sub-category
              materialType: material.pr_material_type_name,
              uom: material.pr_material_type_uom_id,
              uom_name: material.pr_material_type_uom_name,
              totalQtyKg: material.pr_material_type_quantity_by_kg,
              estimatedAmount,
              remark: material.remark,
              diameters: diameterMap,
              isFirstInCity: siteIndex === 0 && materialIndex === 0,
              isFirstInSite: materialIndex === 0,
              rowType: "material",
            });
          });

          transformedData.push({
            key: `${cityIndex}-${siteIndex}-subtotal`,
            city: city.city_name,
            site: `Sub Total for ${site.site_name} site`,
            totalQtyKg: site.site_total_quantity_by_kg,
            estimatedAmount: siteTotalEstimatedAmount,
            rowType: "siteSubTotal",
          });
        });

        transformedData.push({
          key: `${cityIndex}-total`,
          city: `City Total - ${city.city_name}`,
          totalQtyKg: city.city_total_quantity_by_kg,
          estimatedAmount: cityTotalEstimatedAmount,
          rowType: "cityTotal",
        });
        rowCounter++;
      });

      transformedData.push({
        key: "grand-total",
        city: "Total",
        totalQtyKg: prData.city_site_material_data.reduce(
          (sum, city) => sum + city.city_total_quantity_by_kg,
          0
        ),
        estimatedAmount: grandTotalEstimatedAmount,
        rowType: "grandTotal",
      });

      setTableData(transformedData);
    }
  }, [prData]);
  const columns = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      width: 50,
      align: "center",
      render: (text, record) => {
        if (record.isFirstInCity) {
          return text;
        }
        return "";
      },
    },
    {
      title: "City",
      dataIndex: "city",
      width: 120,
      render: (text, record) => {
        if (
          record.rowType === "siteSubTotal" ||
          record.rowType === "cityTotal" ||
          record.rowType === "grandTotal"
        ) {
          return "";
        }
        if (record.rowType === "material" && !record.isFirstInCity) {
          return "";
        }
        return text;
      },
    },
    {
      title: "Site",
      dataIndex: "site",
      width: 180,
      render: (text, record) => {
        if (record.rowType === "grandTotal") {
          return <div className="totalCityOverallSitee5">Total</div>;
        }
        if (record.rowType === "cityTotal") {
          return <div className="citytotal">City Total</div>;
        }
        if (record.rowType === "siteSubTotal") {
          return <div className="subtotal">Sub Total</div>;
        }
        if (record.rowType === "material" && !record.isFirstInSite) {
          return "";
        }
        return text;
      },
    },
    {
      title: "Sub Category",
      dataIndex: "pr_material_type_sub_category",
      width: 150,
      render: (text, record) => {
        if (
          ["siteSubTotal", "cityTotal", "grandTotal"].includes(record.rowType)
        ) {
          return "";
        }
        return text;
      },
    },
    {
      title: "Grade & Quality",
      dataIndex: "materialType",
      width: 150,
      render: (text, record) => {
        if (
          ["siteSubTotal", "cityTotal", "grandTotal"].includes(record.rowType)
        ) {
          return "";
        }
        return text;
      },
    },
    ...materialSizes.map((size) => ({
      title: <span>{size} MM </span>,
      dataIndex: "diameters",
      width: 110,
      align: "center",
      render: (diameters, record) => {
        if (
          ["siteSubTotal", "cityTotal", "grandTotal"].includes(record.rowType)
        ) {
          return "";
        }
        return diameters[size]?.quantity || "";
      },
    })),
    {
      title: <Tooltip title="Total Quantity in Kilograms">Qty. in Kg</Tooltip>,
      dataIndex: "totalQtyKg",
      width: 100,
      align: "center",
      render: (text, record) => {
        if (
          record.rowType === "siteSubTotal" ||
          record.rowType === "cityTotal" ||
          record.rowType === "grandTotal"
        ) {
          return "";
        }
        return (
          text?.toLocaleString("en-IN", { maximumFractionDigits: 0 }) || ""
        );
      },
    },
    {
      title: "UoM.",
      dataIndex: "uom_name",
      width: 80,
      align: "center",
      render: (text, record) => {
        if (
          ["siteSubTotal", "cityTotal", "grandTotal"].includes(record.rowType)
        ) {
          return "";
        }
        return text;
      },
    },
    {
      title: <Tooltip title="Estimated Amount">Est. Amount</Tooltip>,
      dataIndex: "estimatedAmount",
      width: 180,
      align: "right",
      render: (text, record) => {
        if (record.rowType === "material") {
          return `₹ ${text?.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}`;
        }
        if (record.rowType === "siteSubTotal") {
          const siteTotal = record.totalQtyKg * 50;
          return (
            <div style={{ width: "100%", textAlign: "-webkit-right" }}>
              <div className="subtotal">
                ₹ {text.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
            </div>
          );
        }
        if (record.rowType === "cityTotal") {
          const cityTotal = record.totalQtyKg * 50;
          return (
            <div style={{ width: "100%", textAlign: "-webkit-right" }}>
              <div className="citytotal">
                ₹ {text.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
            </div>
          );
        }
        if (record.rowType === "grandTotal") {
          const grandTotal = record.totalQtyKg * 50;
          return (
            <div style={{ width: "100%", textAlign: "-webkit-right" }}>
              <div className="totalCityOverallSitee5">
                ₹ {text.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
            </div>
          );
        }
        return "";
      },
    },
    {
      title: "Remarks",
      dataIndex: "remark",
      width: 150,
       className: "no-transform-remarks",
      render: (text, record) => {
        if (
          ["siteSubTotal", "cityTotal", "grandTotal"].includes(record.rowType)
        ) {
          return "";
        }
        return text || "";
      },
    },
  ];
  

  return (
    <Table
      columns={columns}
      dataSource={tableData}
      pagination={false}
      bordered
      scroll={{ x: 1500 }}
      rowClassName={(record) => {
        if (record.rowType === "siteSubTotal") return "site-subtotal-row";
        if (record.rowType === "cityTotal") return "city-total-row";
        if (record.rowType === "grandTotal") return "grand-total-row";
        return "";
      }}
    />
  );
};

PurchaseRequisitionSteelDetails.propTypes = {
  prData: PropTypes.shape({
    city_site_material_data: PropTypes.arrayOf(
      PropTypes.shape({
        city_name: PropTypes.string.isRequired,
        city_total_quantity: PropTypes.number.isRequired,
        city_total_quantity_by_kg: PropTypes.number.isRequired,
        site_wise_pr: PropTypes.arrayOf(
          PropTypes.shape({
            site_id: PropTypes.number.isRequired,
            site_name: PropTypes.string.isRequired,
            site_total_quantity: PropTypes.number.isRequired,
            site_total_quantity_by_kg: PropTypes.number.isRequired,
            pr_material_details: PropTypes.arrayOf(
              PropTypes.shape({
                pr_material_id: PropTypes.number.isRequired,
                pr_material_type_id: PropTypes.number.isRequired,
                pr_material_type_name: PropTypes.string.isRequired,
                pr_material_type_sub_category: PropTypes.string.isRequired,
                pr_material_type_uom_id: PropTypes.string.isRequired,
                pr_material_type_uom_name: PropTypes.oneOfType([
                  PropTypes.string,
                  PropTypes.number,
                ]).isRequired,
                estimated_material_unit_price: PropTypes.number.isRequired,
                remark: PropTypes.string,
                pr_material_type_quantity: PropTypes.number.isRequired,
                pr_material_type_quantity_by_kg: PropTypes.number.isRequired,
                steel_sizes: PropTypes.arrayOf(
                  PropTypes.shape({
                    value: PropTypes.number.isRequired,
                    diameter_uom: PropTypes.string.isRequired,
                    quantity: PropTypes.number.isRequired,
                    estimated_price: PropTypes.number.isRequired,
                  })
                ).isRequired,
              })
            ).isRequired,
          })
        ).isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default PurchaseRequisitionSteelDetails;
